/**
 * Workshop version of LinkPreviewFrame — URL unfurl list.
 *
 * One link renders as a compact half-tile row; two or more render as a
 * scrollable full-card list.
 */

import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Spinner } from "@/components/ui/spinner";
import {
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import type { FrameStatus } from "@/workshop/components/shapes/shared/frame-utils";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PreviewLink {
  url: string;
  title?: string;
  description?: string;
}

interface LinkPreviewFrameProps {
  url?: string;
  title?: string;
  description?: string;
  links?: PreviewLink[];
  status?: FrameStatus;
  className?: string;
  "data-testid"?: string;
}

// ─── Mock data ────────────────────────────────────────────────────────────────

const MAX_LINKS = 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function domainFromUrl(url: string): string {
  try {
    return new URL(url).hostname.replace("www.", "");
  } catch {
    return url;
  }
}

function faviconLetter(url: string): string {
  return domainFromUrl(url)[0]?.toUpperCase() ?? "?";
}

function toPreviewLink(raw: unknown): PreviewLink | null {
  if (typeof raw !== "object" || raw === null) return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.url !== "string" || !row.url.trim()) return null;
  return {
    url: row.url,
    title: typeof row.title === "string" ? row.title : undefined,
    description:
      typeof row.description === "string" ? row.description : undefined,
  };
}

function LinkRow({ link, index }: { link: PreviewLink; index: number }) {
  const domain = domainFromUrl(link.url);
  return (
    <div
      className="flex min-w-0 shrink-0 items-center gap-2.5 rounded-control bg-elevated/60 px-2.5 py-2"
      data-testid={`link-preview-frame-link-${index}`}
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-preview bg-accent/18"
        aria-hidden="true"
      >
        <span className="text-canvas-xs font-bold leading-none text-accent">
          {faviconLetter(link.url)}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-mono text-canvas-2xs text-muted">
          {domain}
        </p>
        <p className="truncate text-canvas-sm font-semibold text-primary">
          {link.title || domain}
        </p>
        {link.description ? (
          <p className="truncate text-canvas-xs text-muted">
            {link.description}
          </p>
        ) : null}
      </div>
      <Icons.ExternalLink
        className="h-3.5 w-3.5 shrink-0 text-muted"
        aria-hidden="true"
      />
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function LinkPreviewFrame({
  url = "https://www.wythehotel.com/evenings/rooftop",
  title = "The Rooftop at Wythe — Williamsburg, Brooklyn",
  description = "Seasonal cocktails and skyline views above Bedford Ave. Open Thu–Sun, walk-ins welcome before 8pm.",
  links,
  status = "done",
  className,
  "data-testid": testId = "link-preview-frame",
}: LinkPreviewFrameProps) {
  const allLinks: PreviewLink[] = Array.isArray(links)
    ? links
        .map(toPreviewLink)
        .filter((link): link is PreviewLink => link !== null)
        .slice(0, MAX_LINKS)
    : [{ url, title, description }];
  const multi = allLinks.length > 1;

  function renderBody(): ReactNode {
    if (status === "error") {
      return (
        <div className="flex items-center gap-2 py-3" data-testid={`${testId}-error`}>
          <Icons.CircleAlert className="h-3.5 w-3.5 shrink-0 text-status-error" aria-hidden="true" />
          <span className="text-canvas-base text-muted">Couldn&apos;t fetch preview. Try again.</span>
        </div>
      );
    }
    if (status === "streaming") {
      return (
        <div className="flex items-center gap-2 py-3" data-testid={`${testId}-loading`}>
          <Spinner className="h-3 w-3" />
          <span className="text-canvas-base text-muted">Fetching preview…</span>
        </div>
      );
    }
    if (allLinks.length === 0) {
      return (
        <p className="py-3 text-center text-canvas-base italic text-muted" data-testid={`${testId}-empty`}>
          No preview available
        </p>
      );
    }
    if (!multi && allLinks[0]) {
      return <LinkRow link={allLinks[0]} index={0} />;
    }
    return (
      <div
        className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto"
        data-testid={`${testId}-list`}
      >
        {allLinks.map((link, index) => (
          <LinkRow
            key={`${link.url}-${index}`}
            link={link}
            index={index}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      className={cn(surfaceRoleClassName("frame"), "group relative flex flex-col overflow-hidden rounded-frame", className)}
      data-testid={testId}
      {...surfaceRoleAttributes("frame")}
    >
      <div className="flex min-h-0 flex-1 flex-col justify-center gap-2 px-3 py-2.5">
        {renderBody()}
      </div>
    </div>
  );
}

export type { LinkPreviewFrameProps, PreviewLink };
